import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listEmployees, type EmployeeRecord } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';

export function Employees() {
  const { can } = useAuth();
  const [rows, setRows] = useState<EmployeeRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      listEmployees({ search: search || undefined, status: status || undefined })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items);
          setTotal(res.total);
          setError(null);
        })
        .catch((err: Error) => !cancelled && setError(err.message))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, status]);

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="employees" size="xs" />
            <span>Employee Directory</span>
          </span>
          <h1>Staff & Workforce</h1>
        </div>
        <div className="toolbar">
          <input
            aria-label="Search by name or code"
            placeholder="Search name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="probation">Probation</option>
            <option value="notice">Notice</option>
            <option value="exited">Exited</option>
          </select>
          {can('employee.create') && (
            <button className="btn-primary">
              <Icon name="plus" size="xs" />
              <span>Add Employee</span>
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="panel">
        <div className="panel-head">
          <strong>{loading ? 'Loading workforce records…' : `${total} Employee${total === 1 ? '' : 's'} Listed`}</strong>
          <span className="muted" style={{ fontSize: 12 }}>
            Role-scoped visibility active
          </span>
        </div>

        {!loading && rows.length === 0 ? (
          <div className="empty">
            <strong>No employees match your search</strong>
            <p>Clear the filters or add a new employee to this organization.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Code</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Joined</th>
                  <th style={{ width: 110 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link className="record-link" to={`/employees/${row.id}`}>
                        {row.employee_code}
                      </Link>
                    </td>
                    <td>
                      <strong>{row.first_name} {row.last_name}</strong>
                    </td>
                    <td className="muted">{row.designation ?? '—'}</td>
                    <td className="mono">{row.date_of_joining}</td>
                    <td><StatusBadge status={row.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
